import axios, { type AxiosError } from 'axios';
import { API_BASE_URL, DEFAULT_TIMEOUT_MS, LONG_TIMEOUT_MS } from '../config/env';
import { useAuthStore } from '../auth/authStore';
import { setPath } from './setPath';
import { ApiError, type ResponseBase, type SessionHeader } from './types/common';

export const httpClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: DEFAULT_TIMEOUT_MS,
  headers: { 'Content-Type': 'application/json' },
});

/**
 * Confirmed empirically against the real API: requests bind case-insensitively
 * (camelCase bodies work fine), but every JSON response comes back PascalCase
 * (ASP.NET Web API's default serializer casing). Normalizing here means every TS
 * type in api/types/* can stay idiomatic camelCase instead of mirroring C# casing.
 */
function toCamelCase(input: unknown): unknown {
  if (Array.isArray(input)) return input.map(toCamelCase);
  if (input !== null && typeof input === 'object') {
    return Object.fromEntries(
      Object.entries(input as Record<string, unknown>).map(([key, value]) => [
        key.charAt(0).toLowerCase() + key.slice(1),
        toCamelCase(value),
      ])
    );
  }
  return input;
}

httpClient.interceptors.response.use((response) => {
  // Binary responses (PDF/Excel/log dumps via apiPostBlob) must pass through untouched —
  // toCamelCase would walk a Blob's (empty) enumerable keys and hand back `{}`.
  if (response.config.responseType === 'blob' || response.data instanceof Blob) return response;
  response.data = toCamelCase(response.data);
  return response;
});

export interface ApiCallOptions<TReq> {
  /** Dot-path to nest sessionHeader at (e.g. 'request.sessionHeader'), or a fn escape hatch.
   *  Default: merged as a top-level `sessionHeader` field — covers the common case. */
  sessionPath?: string | ((body: TReq, session: SessionHeader) => TReq);
  /** Login, Insurance/GetCountries, api/airlinestats — no session to inject yet/at all. */
  skipSession?: boolean;
  timeoutMs?: number;
  headers?: Record<string, string>;
}

function withSession<TReq extends object>(body: TReq, options?: ApiCallOptions<TReq>): TReq {
  if (options?.skipSession) return body;

  const session = useAuthStore.getState().session;
  if (!session) return body;

  const { sessionPath } = options ?? {};
  if (typeof sessionPath === 'function') return sessionPath(body, session);
  if (typeof sessionPath === 'string') return setPath(body, sessionPath, session);
  return { ...body, sessionHeader: session };
}

function errorMessage(route: string, response: ResponseBase, fallback: string): string {
  return response.serviceError?.errorMessages?.filter(Boolean).join(' ') || fallback;
}

/**
 * API "authenticationError" bayrağı ya da HTTP 401 → sunucu oturumu düşmüş. Sadece yerel
 * durumda hâlâ authenticated görünüyorsak authStore'u temizle — `RequireAuth` bunu görüp
 * `/login`'e yönlendirir. Fırlatılan `ApiError` yine de propagate olur (çağıran kod
 * isterse mesajı gösterir; redirect zaten mount'u söker).
 */
function maybeHandleAuthExpiry(err: unknown): void {
  if (useAuthStore.getState().status !== 'authenticated') return;
  const axiosErr = err as AxiosError<ResponseBase>;
  const flagged =
    (err instanceof ApiError && !!err.serviceError?.errorFlags?.authenticationError) ||
    axiosErr?.response?.status === 401 ||
    !!axiosErr?.response?.data?.serviceError?.errorFlags?.authenticationError;
  if (flagged) useAuthStore.getState().logout();
}

function toApiError(route: string, err: unknown): ApiError {
  maybeHandleAuthExpiry(err);
  if (err instanceof ApiError) return err;
  const axiosErr = err as AxiosError<ResponseBase>;
  const data = axiosErr.response?.data;
  if (data && typeof data === 'object' && 'hasError' in data) {
    return new ApiError(errorMessage(route, data, `${route} failed`), data.serviceError);
  }
  return new ApiError(axiosErr.message || `${route} request failed`);
}

/** Core POST helper — every backend route is POST (see migration guide Bölüm 7.1). */
export async function apiPost<TReq extends object, TRes extends ResponseBase>(
  route: string,
  body: TReq,
  options?: ApiCallOptions<TReq>
): Promise<TRes> {
  const finalBody = withSession(body, options);
  try {
    const response = await httpClient.post<TRes>(route, finalBody, {
      timeout: options?.timeoutMs ?? DEFAULT_TIMEOUT_MS,
      headers: options?.headers,
    });
    if (response.data?.hasError) {
      throw new ApiError(
        errorMessage(route, response.data, `${route} returned hasError: true`),
        response.data.serviceError
      );
    }
    return response.data;
  } catch (err) {
    throw toApiError(route, err);
  }
}

export interface BlobResponse {
  blob: Blob;
  /** Parsed from Content-Disposition, when the server sends one. */
  filename?: string;
  contentType: string;
}

function parseFilename(disposition?: string): string | undefined {
  if (!disposition) return undefined;
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(disposition);
  if (star?.[1]) return decodeURIComponent(star[1].replace(/["']/g, '').trim());
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain?.[1]?.trim();
}

/**
 * Binary POST — PDF (e-ticket / hotel voucher / offer), Excel (reports), log dumps.
 * Same session injection as apiPost; the response interceptor skips camel-casing for
 * blobs (see above). If the backend answers an error as JSON despite responseType:'blob',
 * the body is read back as text and surfaced as an ApiError.
 */
export async function apiPostBlob<TReq extends object>(
  route: string,
  body: TReq,
  options?: ApiCallOptions<TReq>
): Promise<BlobResponse> {
  const finalBody = withSession(body, options);
  try {
    const response = await httpClient.post<Blob>(route, finalBody, {
      responseType: 'blob',
      timeout: options?.timeoutMs ?? LONG_TIMEOUT_MS,
      headers: options?.headers,
    });
    const raw = response.data as Blob | string;
    const data = raw instanceof Blob ? raw : new Blob([String(raw)], { type: 'text/plain' });
    const contentType = String(
      response.headers['content-type'] ?? (raw instanceof Blob ? raw.type : '') ?? 'application/octet-stream'
    );
    // A backend error despite responseType:'blob' comes back as a JSON envelope — read it
    // back and surface it as an ApiError. Only JSON (not e.g. text/csv success payloads).
    if (contentType.includes('application/json') && typeof data.text === 'function') {
      const text = await data.text();
      try {
        const parsed = JSON.parse(text) as ResponseBase;
        if (parsed?.hasError) {
          throw new ApiError(errorMessage(route, parsed, `${route} failed`), parsed.serviceError);
        }
      } catch (parseErr) {
        if (parseErr instanceof ApiError) throw parseErr;
      }
    }
    return {
      blob: data,
      filename: parseFilename(
        response.headers['content-disposition'] ? String(response.headers['content-disposition']) : undefined
      ),
      contentType,
    };
  } catch (err) {
    throw toApiError(route, err);
  }
}

/** multipart/form-data variant — only known use so far: CorporateEmployee/ImportCorporateEmployeesFromExcel.
 *  sessionHeader placement for multipart is unconfirmed against the real API; defaults to flat fields. */
export async function apiPostMultipart<TRes extends ResponseBase>(
  route: string,
  formData: FormData,
  options?: { timeoutMs?: number }
): Promise<TRes> {
  const session = useAuthStore.getState().session;
  if (session) {
    formData.append('sessionId', session.sessionId);
    formData.append('sessionToken', session.sessionToken);
  }
  try {
    const response = await httpClient.post<TRes>(route, formData, {
      timeout: options?.timeoutMs ?? LONG_TIMEOUT_MS,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (response.data?.hasError) {
      throw new ApiError(
        errorMessage(route, response.data, `${route} returned hasError: true`),
        response.data.serviceError
      );
    }
    return response.data;
  } catch (err) {
    throw toApiError(route, err);
  }
}

/**
 * Some routes' request DTO IS literally a bare `SessionHeader` (e.g. Airport/Get,
 * Bank/GetBanks, CorporateDepartment/GetDepartments, Authenticated/Current) — no
 * wrapper field, the whole body is just {sessionId, sessionToken}. Distinct from the
 * far more common case (apiPost's default) where SessionHeader is one field among
 * several in a larger request DTO.
 */
export async function apiPostSession<TRes extends ResponseBase>(
  route: string,
  options?: { timeoutMs?: number }
): Promise<TRes> {
  const session = useAuthStore.getState().session;
  try {
    const response = await httpClient.post<TRes>(route, session ?? {}, {
      timeout: options?.timeoutMs ?? DEFAULT_TIMEOUT_MS,
    });
    if (response.data?.hasError) {
      throw new ApiError(
        errorMessage(route, response.data, `${route} returned hasError: true`),
        response.data.serviceError
      );
    }
    return response.data;
  } catch (err) {
    throw toApiError(route, err);
  }
}

/**
 * Session-less GET helper. Shares the same `/backend-api` base, axios instance and
 * PascalCase→camelCase response interceptor as the POST helpers, but does a real GET
 * and does NOT apply the `hasError` envelope check — the caller inspects the real HTTP
 * status (a non-2xx still throws, via `toApiError`). Only two confirmed GET routes:
 * `api/airlinestats` (`X-Api-Key` header) and `Insurance/GetCountries`.
 */
export async function apiGet<T>(
  route: string,
  opts?: { params?: Record<string, string | number | undefined>; headers?: Record<string, string> }
): Promise<T> {
  try {
    const response = await httpClient.get<T>(route, {
      params: opts?.params,
      headers: opts?.headers,
    });
    return response.data;
  } catch (err) {
    throw toApiError(route, err);
  }
}
